import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ResponsivePreviewSwitch} from '@Components/event-wizard/responsive-preview-switch';
import type {PreviewProps} from '@Components/event-wizard/preview/preview.types';
import {PREVIEW_IFRAME_ID, PREVIEW_SHORTCUTS} from '@Components/event-wizard/preview/preview.types';
import {
  enablePreviewShortcuts,
  syncPreviewTicketingQuestions,
  updatePreviewCountdown,
  updatePreviewCoverPhoto,
  updatePreviewDateTime,
  updatePreviewDescription,
  updatePreviewOrganiserDetails,
  updatePreviewRegisterButton,
  updatePreviewTimeZone,
  updatePreviewTitle,
  updatePreviewVenue,
  updatePreviewVenueAddressDetails,
  updatePreviewWatermark,
  updateStyleColor,
  updateStyleFont,
} from '@Components/event-wizard/preview/preview.helper';
import {getGMTLabel} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {
  useEventWizardCoverPhoto,
  useEventWizardCurrentProgressStep,
  useEventWizardDescription,
  useEventWizardEndDateTime,
  useEventWizardIsDesignPrefillContentLoading,
  useEventWizardIsRemoveWatermark,
  useEventWizardOrganisers,
  useEventWizardRecurringData,
  useEventWizardRegistrationDetails,
  useEventWizardSelectedRegistrationMode,
  useEventWizardStartDateTime,
  useEventWizardStyles,
  useEventWizardTicketingConfig,
  useEventWizardTicketingFees,
  useEventWizardTimeZone,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {SkeletonBrick} from '@Components/skeleton-brick';
import styles from './preview.module.scss';
import {EventPageSource, getEventPageURLWithSource} from '@Libraries/events.library';
import {EventWizardProgressStep} from '@Components/event-wizard/event-wizard.types';

export function Preview({isMobileScreenSize = false}: PreviewProps): ReactElement {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isMobilePreview, setIsMobilePreview] = useState<boolean>(true);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(true);

  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const timezone = useEventWizardTimeZone();
  const style = useEventWizardStyles();
  const organisers = useEventWizardOrganisers();
  const title = useEventWizardTitle();
  const description = useEventWizardDescription();
  const venue = useEventWizardVenue();
  const coverPhoto = useEventWizardCoverPhoto();
  const registrationForm = useEventWizardRegistrationDetails();
  const currentProgressStep = useEventWizardCurrentProgressStep();
  const selectedRegistrationMode = useEventWizardSelectedRegistrationMode();
  const ticketingConfig = useEventWizardTicketingConfig();
  const ticketingFees = useEventWizardTicketingFees();
  const isRemoveWatermark = useEventWizardIsRemoveWatermark();
  const isDesignPrefillContentLoading = useEventWizardIsDesignPrefillContentLoading();

  const onIframeLoad = (): void => {
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: recurringPattern,
      timeZone: timezone,
    });
    updatePreviewCountdown(selectedStartDateTime, timezone);
    updateStyleColor(style.colors);
    updateStyleFont(style.fontFamily);
    updatePreviewOrganiserDetails(organisers.selectedOrganiser);
    updatePreviewWatermark(isRemoveWatermark);

    // Shows the cover slot on every step, with the placeholder until there is a cover: step 1 has the cover tile.
    void updatePreviewCoverPhoto(coverPhoto.uploadedUrl);

    updatePreviewTitle(title);
    updatePreviewDescription(description);
    updatePreviewVenue(venue);

    if (venue.offlineVenue?.address) {
      updatePreviewVenueAddressDetails(venue.offlineVenue.address);
    }
    updatePreviewTimeZone(getGMTLabel(timezone));
    updatePreviewWatermark(isRemoveWatermark);
    updatePreviewRegisterButton(registrationForm, selectedRegistrationMode, ticketingConfig);
    setIsPreviewLoading(false);
  };

  useEffect(() => {
    if (isPreviewLoading) {
      return;
    }
    syncPreviewTicketingQuestions({
      config: ticketingConfig,
      mode: selectedRegistrationMode,
      step: currentProgressStep,
      eventTitle: title,
      startDateTime: selectedStartDateTime,
      coverPhotoUrl: coverPhoto.uploadedUrl,
      ticketingFees,
    });
    updatePreviewRegisterButton(registrationForm, selectedRegistrationMode, ticketingConfig);
  }, [isPreviewLoading, currentProgressStep, selectedRegistrationMode, ticketingConfig, registrationForm, title, selectedStartDateTime, coverPhoto.uploadedUrl, ticketingFees]);

  /**
   * Clicking the cover, title or date in the preview takes the user to the step 1 field that sets it, so the shortcuts
   * work only where those fields are: step 1, once the form is no longer a skeleton. The full-screen preview on small
   * screens covers the form, so it is left out.
   */
  const canPreviewShortcutsReachFields = !isPreviewLoading && !isDesignPrefillContentLoading && !isMobileScreenSize && currentProgressStep === EventWizardProgressStep.OVERVIEW;

  useEffect(() => {
    if (!canPreviewShortcutsReachFields) {
      return undefined;
    }
    return enablePreviewShortcuts(PREVIEW_SHORTCUTS);
  }, [canPreviewShortcutsReachFields]);

  const toggleMobileDesktopPreview = (): void => {
    setIsMobilePreview(!isMobilePreview);
  };

  const getLoadingScreen = (): ReactElement | undefined => {
    return isPreviewLoading || isDesignPrefillContentLoading ? <SkeletonBrick className="_full-height _full-width _position-absolute" /> : undefined;
  };

  return (
    <div className={`${styles.previewSectionContainer} flex-row-align-center`}>
      <div
        className={`border-s-standard ${styles.previewSection} ${isMobilePreview ? 'radius-16' : 'radius-12'} ${isMobileScreenSize ? styles.previewSectionMobileScreen : 'spacing-p-t-10 spacing-p-l-10 spacing-p-r-10 bg-neutral-1'}`}
      >
        <div className={isMobileScreenSize ? '' : styles.previewContainer}>
          {!isMobileScreenSize ? <ResponsivePreviewSwitch isMobileSelected={isMobilePreview} onChange={toggleMobileDesktopPreview} /> : undefined}
          <div className={`${styles.preview} ${!isMobileScreenSize ? styles.previewBorder : ''} ${!isMobileScreenSize && isMobilePreview ? styles.previewMobile : ''}`}>
            <div className={`${styles.iframeContainer} ${!isMobileScreenSize && isMobilePreview ? styles.iframeContainerMobilePreview : styles.iframeContainerPreview}`}>
              {getLoadingScreen()}
              <iframe
                ref={iframeRef}
                id={PREVIEW_IFRAME_ID}
                src={getEventPageURLWithSource(EventPageSource.FOR_WIZARD_IFRAME)}
                onLoad={onIframeLoad}
                title="Example Site"
                className={styles.iframe}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
